// @ts-check
// =============================================================================
// js/net/startup-bundle.js — download the duel-asset bundle once, unpack it,
// and pre-seed PIXI so the duel loads from memory instead of the network.
//
// WHAT THIS IS
//   The client half of Startup.zip (server: src/http/startup-bundle.js). One
//   fetch pulls every duel's shared chrome — the card/board frame textures, the
//   battlefield frame, the bar buttons + tick clock, the leader portraits, and
//   the art placeholders — instead of ~50 individual requests spread across the
//   first duel mount. We unpack the zip in memory and register each file into
//   PIXI's loader cache under the exact URL its consumer will ask for, so every
//   later `PIXI.Assets.load('/cardframes2/…')` / generator frame load / clock
//   `Assets.get('/gui/clock/…')` resolves instantly with no server round-trip.
//
// HOW THE SEEDING WORKS (PIXI v8 internals, verified against the bundled lib)
//   PIXI.Assets.load(url) resolves the URL to a src, then hands it to
//   loader.load(); the loader only SKIPS the network when its promiseCache
//   already holds the resolved (absolutized) key. So for each unpacked file we
//   build a Texture from its bytes and drop a resolved promise into
//   loader.promiseCache[path.toAbsolute(url)] — plus Cache.set(url) for the
//   `Assets.get()` callers (the tick clock). This survives the card generator
//   re-registering its own aliases (frameFree → /cardframes2/Frame_Free.png):
//   the alias still resolves to that URL, whose promiseCache entry is ours.
//
// FAIL-SOFT
//   Any failure (offline, decode error, older server without the route) resolves
//   quietly — the duel then just loads each asset the old way. Idempotent: the
//   work runs at most once per page load.
// =============================================================================

import * as PIXI from 'pixi.js';

/** The single in-flight/settled attempt (idempotent). */
let bundlePromise = /** @type {Promise<number>|null} */ (null);

/**
 * Register the asset service worker (public/sw.js). It serves the bundled paths
 * — frames, gui, leader portraits + cards, placeholders — from a versioned cache
 * primed by Startup.zip, so DOM <img> usages (lobby, ladder, gallery, profile,
 * deck-list, the leader-card zoom) come from the ONE bundle download instead of
 * a request per file. Idempotent + fail-soft; poke it each load so a freshly
 * deployed bundle is picked up. No-op where service workers are unavailable.
 * @returns {void}
 */
export function registerAssetWorker() {
	if (!('serviceWorker' in navigator)) return;
	navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' })
		.then((reg) => {
			const poke = () => (reg.active || navigator.serviceWorker.controller)?.postMessage('prime');
			poke();
			navigator.serviceWorker.ready.then(poke).catch(() => {});
		})
		.catch(() => {
			// Registration blocked (private mode, insecure context) — assets load
			// normally; the PIXI-side seeding still bundles the duel chrome.
		});
}

/**
 * Parse a STORE-method ZIP (what the server writes) by walking its local file
 * headers in order. No compression to inflate; each entry's bytes are raw.
 * @param {ArrayBuffer} buf
 * @returns {Array<{ name: string, bytes: Uint8Array }>}
 */
function parseStoreZip(buf) {
	const dv = new DataView(buf);
	const bytes = new Uint8Array(buf);
	const decoder = new TextDecoder();
	const files = [];
	let o = 0;
	while (o + 4 <= dv.byteLength) {
		if (dv.getUint32(o, true) !== 0x04034b50) break; // reached the central directory
		const nameLen = dv.getUint16(o + 26, true);
		const extraLen = dv.getUint16(o + 28, true);
		const size = dv.getUint32(o + 18, true); // compressed size (= raw, STORE)
		const nameStart = o + 30;
		const name = decoder.decode(bytes.subarray(nameStart, nameStart + nameLen));
		const dataStart = nameStart + nameLen + extraLen;
		files.push({ name, bytes: bytes.subarray(dataStart, dataStart + size) });
		o = dataStart + size;
	}
	return files;
}

/** Guess a MIME type from the filename (all bundle entries are images). */
function mimeFor(name) {
	if (/\.png$/i.test(name)) return 'image/png';
	if (/\.webp$/i.test(name)) return 'image/webp';
	if (/\.jpe?g$/i.test(name)) return 'image/jpeg';
	return 'application/octet-stream';
}

/**
 * Turn one unpacked file into a Texture and register it so any later load of
 * its server URL is served from memory.
 * @param {{ name: string, bytes: Uint8Array }} entry
 * @returns {Promise<void>}
 */
async function seedAsset(entry) {
	const url = '/' + entry.name; // e.g. "/cardframes2/Frame_Free.png"
	// Copy out of the zip's shared buffer so the Blob owns its bytes.
	const blob = new Blob([entry.bytes.slice()], { type: mimeFor(entry.name) });
	const bitmap = await createImageBitmap(blob);
	const texture = PIXI.Texture.from(bitmap);

	const resolved = Promise.resolve(texture);
	const abs = PIXI.path.toAbsolute(url);
	// The loader keys its promise cache by the absolutized src; seed both the
	// absolute and raw forms so whichever a consumer resolves to hits.
	PIXI.Assets.loader.promiseCache[abs] = { promise: resolved, parser: null };
	if (abs !== url) {
		PIXI.Assets.loader.promiseCache[url] = { promise: resolved, parser: null };
	}
	// `Assets.get(url)` (the tick clock) reads the global Cache directly.
	PIXI.Assets.cache.set(url, texture);
}

/**
 * Download + unpack Startup.zip and seed PIXI, at most once per page load.
 * Resolves with the number of assets seeded (0 on any failure).
 * @returns {Promise<number>}
 */
export function ensureStartupBundle() {
	if (bundlePromise) return bundlePromise;
	bundlePromise = (async () => {
		try {
			// Frames get minified hard in the duel — mips keep them crisp (the same
			// default the card renderer sets; set it here too since the bundle can
			// seed textures before that init runs).
			PIXI.TextureSource.defaultOptions.autoGenerateMipmaps = true;

			const response = await fetch('/Startup.zip');
			if (!response.ok) return 0;
			const files = parseStoreZip(await response.arrayBuffer());
			let seeded = 0;
			// Decode a few at a time — enough to be quick, gentle on the main thread.
			const queue = [...files];
			await Promise.all(Array.from({ length: 4 }, async () => {
				while (queue.length) {
					const entry = queue.shift();
					if (!entry) continue;
					try {
						await seedAsset(entry);
						seeded++;
					} catch {
						// One bad entry falls back to its normal on-demand load.
					}
				}
			}));
			return seeded;
		} catch {
			return 0; // offline / no route — the duel loads assets the old way
		}
	})();
	return bundlePromise;
}
